import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock, AlertCircle, CheckCircle } from "lucide-react";
import { useState, useEffect } from "react";
import { useParams, useLocation } from "wouter";
import { toast } from "sonner";
import { useAuth } from "@/_core/hooks/useAuth";

export default function InviteAccept() {
  const { token } = useParams<{ token: string }>();
  const [, setLocation] = useLocation();
  const { refresh } = useAuth(); // Refresh auth state after accepting invite

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [inviteData, setInviteData] = useState<{ email: string; role: string } | null>(null);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Fetch invite data on mount
  useEffect(() => {
    const fetchInvite = async () => {
      try {
        // Read response as text first to handle cases where server returns HTML
        const response = await fetch(`/api/auth/invites/${token}`);
        const text = await response.text();

        if (!response.ok) {
          // Non-2xx responses - try to parse as JSON, but fall back gracefully if HTML
          try {
            const errorData = JSON.parse(text);
            throw new Error(errorData.error || "Failed to validate invite");
          } catch (jsonErr) {
            // JSON parse failed, likely HTML - treat as invalid invite
            setError("This invite is invalid or has expired.");
            return;
          }
        }

        // Safe to parse JSON only when response is ok (2xx)
        try {
          const data = JSON.parse(text);
          setInviteData(data);
        } catch (jsonErr) {
          // If ok but not JSON, treat as invalid (unexpected response)
          setError("This invite is invalid or has expired.");
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "Invalid invite");
      } finally {
        setLoading(false);
      }
    };

    fetchInvite();
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    // Validate passwords match
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    // Validate password length
    if (password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }

    setSubmitting(true);

    try {
      const response = await fetch(`/api/auth/invites/${token}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ password }),
      });

      // Read as text first to handle HTML responses
      const text = await response.text();

      if (!response.ok) {
        // Try to parse error response, fall back gracefully for non-JSON
        try {
          const errorData = JSON.parse(text);
          throw new Error(errorData.error || "Failed to accept invite");
        } catch (jsonErr) {
          // JSON parse failed, likely HTML - treat as generic error
          setError("Failed to accept invite. It may be expired or already used.");
          return;
        }
      }

      // On success, redirect even if response isn't perfect JSON
      try {
        JSON.parse(text); // Optional: just validate it's JSON if present
      } catch (jsonErr) {
        // Still fine - proceed on 2xx status
      }

      // Success - refresh auth and redirect to home
      await refresh(); // Update auth state
      setLocation("/");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to accept invite");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 flex flex-col items-center gap-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
            <p className="text-muted-foreground">Loading invite...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center gap-4">
              <AlertCircle className="h-12 w-12 text-destructive" />
              <div className="text-center">
                <h2 className="text-xl font-semibold mb-2">Invalid Invite</h2>
                <p className="text-muted-foreground">{error}</p>
              </div>
              <Button
                variant="outline"
                onClick={() => setLocation("/login")}
                className="w-full"
              >
                Go to Login
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!inviteData) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CheckCircle className="h-12 w-12 text-green-600 mx-auto mb-2" />
          <CardTitle className="text-2xl font-bold">You're Invited!</CardTitle>
          <CardDescription>
            Create your password to join as a {inviteData.role}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
                {error}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={inviteData.email}
                disabled
                className="bg-muted"
              />
              <p className="text-xs text-muted-foreground">
                You'll log in as: {inviteData.email}
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="password"
                  type="password"
                  placeholder="•••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10"
                  required
                  disabled={submitting}
                  minLength={8}
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Must be at least 8 characters
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="•••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="pl-10"
                  required
                  disabled={submitting}
                  minLength={8}
                />
              </div>
            </div>
            <Button
              type="submit"
              className="w-full"
              size="lg"
              disabled={submitting}
            >
              {submitting ? "Creating account..." : "Create Account & Login"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
